import React, {useEffect, useState} from "react";
import {Button, Col, DatePicker, Form, InputNumber, Modal, notification, Row, Select, Spin, Descriptions, Tag, Card, Table} from "antd";
import {ClockCircleOutlined} from "@ant-design/icons";
import {billingServices} from "../../../services/billing.services";
import ModalDepositHistory from "../deposit-history";
import ModalSubscriptionHistory from "../subscription-history";
import ModalInvoicesHistory from "../invoices-history";
import i18n from "../../../i18n";
import moment from "moment";

const SUBSCRIPTION_TYPES = [
    {value: 'regular', label: 'Обычная'},
    {value: 'trial', label: 'Пробная'}
];

export default function ModalSubscriptionInfo({legalEntityId, legalEntityName, partnerId, isShow, setIsShow}) {
    const [loading, setLoading] = useState(false);
    const [subscription, setSubscription] = useState(null);
    const [tariffs, setTariffs] = useState([]);
    const [legalEntities, setLegalEntities] = useState([]);
    const [deposit, setDeposit] = useState(null);
    const [invoices, setInvoices] = useState([]);
    const [subscribing, setSubscribing] = useState(false);
    const [editing, setEditing] = useState(false);
    const [creatingInvoice, setCreatingInvoice] = useState(false);
    const [markingInvoiceId, setMarkingInvoiceId] = useState(null);
    const [cancellingInvoiceId, setCancellingInvoiceId] = useState(null);
    const [isShowDepositHistory, setIsShowDepositHistory] = useState(false);
    const [isShowSubscriptionHistory, setIsShowSubscriptionHistory] = useState(false);
    const [isShowInvoicesHistory, setIsShowInvoicesHistory] = useState(false);

    // Form state
    const [editType, setEditType] = useState(null);
    const [editDateEnd, setEditDateEnd] = useState(null);
    const [editCustomPrice, setEditCustomPrice] = useState(null);
    const [editLegalEntityId, setEditLegalEntityId] = useState(null);
    const [saving, setSaving] = useState(false);

    // Subscribe form state
    const [formTariffId, setFormTariffId] = useState(null);
    const [formType, setFormType] = useState('regular');
    const [formDateStart, setFormDateStart] = useState(moment());
    const [formDateEnd, setFormDateEnd] = useState(null);
    const [formCustomPrice, setFormCustomPrice] = useState(null);
    const [formLegalEntityId, setFormLegalEntityId] = useState(null);

    useEffect(() => {
        if (isShow && legalEntityId) {
            loadSubscription();
            loadTariffs();
            loadDeposit();
            loadInvoices();
            if (partnerId) {
                loadLegalEntities();
            }
            setEditing(false);
            // Сброс формы добавления к значениям по умолчанию
            setFormTariffId(null);
            setFormType('regular');
            setFormDateStart(moment());
            setFormDateEnd(null);
            setFormCustomPrice(null);
            setFormLegalEntityId(legalEntityId);
        }
    }, [isShow, legalEntityId, partnerId]);

    // Безопасное извлечение payload из ответа API: { status, data } -> data
    const getPayload = (response) => {
        if (response && response.data && response.data.status === 'success') {
            return response.data.data;
        }
        return null;
    };

    // Аналог nullish coalescing (??), который запрещён соглашениями проекта
    const or = (value, fallback) => {
        return value === null || value === undefined ? fallback : value;
    };

    const formatAmount = (value) => {
        if (value === null || value === undefined) {
            return '—';
        }
        return Number(value).toLocaleString('ru-RU', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }) + ' ₽';
    };

    const loadSubscription = () => {
        setLoading(true);
        billingServices.getSubscriptionByLegalEntity(legalEntityId)
            .then((response) => {
                setSubscription(getPayload(response));
                setLoading(false);
            })
            .catch(() => {
                setSubscription(null);
                setLoading(false);
            });
    };

    const loadTariffs = () => {
        billingServices.getTariffList()
            .then((response) => {
                setTariffs(getPayload(response) || []);
            })
            .catch(() => {});
    };

    const loadLegalEntities = () => {
        billingServices.getLegalEntitiesByPartner(partnerId)
            .then((response) => {
                setLegalEntities(getPayload(response) || []);
            })
            .catch(() => {
                setLegalEntities([]);
            });
    };

    const loadDeposit = () => {
        billingServices.getDeposit(legalEntityId)
            .then((response) => {
                setDeposit(getPayload(response));
            })
            .catch(() => {
                setDeposit(null);
            });
    };

    const loadInvoices = () => {
        billingServices.getInvoices(legalEntityId)
            .then((response) => {
                setInvoices(getPayload(response) || []);
            })
            .catch(() => {
                setInvoices([]);
            });
    };

    const handleSubscribe = async () => {
        if (!formTariffId || !formType || !formLegalEntityId) {
            notification.warning({
                message: 'Заполните поля',
                description: 'Необходимо выбрать юр. лицо, тариф и тип подписки',
            });
            return;
        }

        setSubscribing(true);
        try {
            const data = {
                type: formType,
                dateStart: formDateStart ? formDateStart.format('YYYY-MM-DD') : null,
                dateEnd: formDateEnd ? formDateEnd.format('YYYY-MM-DD') : null,
            };
            if (formCustomPrice !== null && formCustomPrice !== undefined) {
                data.customPrice = formCustomPrice;
            }
            const response = await billingServices.subscribe(formTariffId, formLegalEntityId, data);
            if (response.data && response.data.status === 'success') {
                notification.success({
                    message: 'Подписка оформлена',
                    description: response.data.message || 'Счет на оплату сформирован',
                });
                loadSubscription();
                // Подписка могла создать/обновить депозит и счёт — перезагружаем
                loadDeposit();
                loadInvoices();
            } else {
                notification.error({
                    message: 'Ошибка',
                    description: response.data.message || 'Не удалось оформить подписку',
                });
            }
        } catch (error) {
            notification.error({
                message: 'Ошибка',
                description: error || 'Не удалось оформить подписку',
            });
        }
        setSubscribing(false);
    };

    const startEditing = () => {
        setEditType(subscription.type);
        setEditDateEnd(subscription.dateEnd ? moment(subscription.dateEnd, 'DD.MM.YYYY') : null);
        setEditCustomPrice(subscription.customPrice);
        setEditLegalEntityId(subscription.legalEntityId);
        setEditing(true);
    };

    const cancelEditing = () => {
        setEditing(false);
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            const data = {};
            if (editType !== subscription.type) data.type = editType;
            if (editDateEnd) data.dateEnd = editDateEnd.format('YYYY-MM-DD HH:mm:ss');
            else data.dateEnd = null;
            if (editCustomPrice !== subscription.customPrice) data.customPrice = editCustomPrice;
            if (editLegalEntityId !== subscription.legalEntityId) data.legalEntityId = editLegalEntityId;

            const response = await billingServices.updateSubscription(subscription.id, data);
            if (response.data && response.data.status === 'success') {
                notification.success({message: 'Подписка обновлена'});
                setEditing(false);
                loadSubscription();
                // Данные депозита и счетов могли измениться — перезагружаем
                loadDeposit();
                loadInvoices();
            } else {
                notification.error({
                    message: 'Ошибка',
                    description: response.data.message || 'Не удалось обновить подписку',
                });
            }
        } catch (error) {
            notification.error({
                message: 'Ошибка',
                description: error || 'Не удалось обновить подписку',
            });
        }
        setSaving(false);
    };

    const handleCreateInvoice = async () => {
        if (!subscription) {
            return;
        }

        setCreatingInvoice(true);
        try {
            const response = await billingServices.createInvoice(subscription.id);
            if (response.data && response.data.status === 'success') {
                // created=false — у подписки уже есть неоплаченный счёт, дубликат не создан
                const created = response.data.data && response.data.data.created;
                notification[created ? 'success' : 'info']({
                    message: created ? 'Счёт сформирован' : 'Счёт уже есть',
                    description: response.data.message,
                });
                if (created) {
                    loadInvoices();
                }
            } else {
                notification.error({
                    message: 'Ошибка',
                    description: (response.data && response.data.message) || 'Не удалось создать счёт',
                });
            }
        } catch (error) {
            notification.error({
                message: 'Ошибка',
                description: error || 'Не удалось создать счёт',
            });
        }
        setCreatingInvoice(false);
    };

    const handleMarkPaid = (invoice) => {
        Modal.confirm({
            title: 'Подтвердите оплату',
            content: `Отметить счёт №${invoice.number} оплаченным? Сумма ${formatAmount(invoice.amount)} будет зачислена на депозит.`,
            okText: 'Оплачено',
            cancelText: 'Отмена',
            onOk: () => markInvoice(invoice),
        });
    };

    const markInvoice = async (invoice) => {
        setMarkingInvoiceId(invoice.id);
        try {
            const response = await billingServices.markInvoicePaid(invoice.id);
            if (response.data && response.data.status === 'success') {
                const data = response.data.data;
                const description = data && data.chargedSubscription
                    ? 'Стоимость подписки списана с депозита, срок продлён.'
                    : 'Сумма зачислена на депозит.';
                notification.success({
                    message: 'Счёт отмечен оплаченным',
                    description: description,
                });
                // Состояние депозита, счетов и подписки могло измениться — перезагружаем
                loadInvoices();
                loadDeposit();
                loadSubscription();
            } else {
                notification.error({
                    message: 'Ошибка',
                    description: (response.data && response.data.message) || 'Не удалось отметить счёт',
                });
            }
        } catch (error) {
            notification.error({
                message: 'Ошибка',
                description: error || 'Не удалось отметить счёт',
            });
        }
        setMarkingInvoiceId(null);
    };

    const handleCancelInvoice = (invoice) => {
        Modal.confirm({
            title: 'Отмена счёта',
            content: `Отменить счёт №${invoice.number} на сумму ${formatAmount(invoice.amount)}? Отменённый счёт нельзя будет восстановить.`,
            okText: 'Отменить счёт',
            okType: 'danger',
            cancelText: 'Закрыть',
            onOk: () => performCancelInvoice(invoice),
        });
    };

    const performCancelInvoice = async (invoice) => {
        setCancellingInvoiceId(invoice.id);
        try {
            const response = await billingServices.cancelInvoice(invoice.id);
            if (response.data && response.data.status === 'success') {
                notification.success({
                    message: 'Счёт отменён',
                    description: response.data.message || 'Счёт №' + invoice.number + ' отменён',
                });
                // Отменённый счёт исчезает из списка активных — перезагружаем
                loadInvoices();
            } else {
                notification.error({
                    message: 'Ошибка',
                    description: (response.data && response.data.message) || 'Не удалось отменить счёт',
                });
            }
        } catch (error) {
            notification.error({
                message: 'Ошибка',
                description: error || 'Не удалось отменить счёт',
            });
        }
        setCancellingInvoiceId(null);
    };

    return (
        <Modal
            title="Управление подпиской"
            cancelText={i18n.t('main.buttons.close')}
            className="modal-window"
            visible={isShow}
            onCancel={() => setIsShow(false)}
            footer={null}
            width={720}
        >
            <Spin spinning={loading} tip={i18n.t('main.loadText.loading')}>
                {subscription ? (
                    <div>
                        {editing ? (
                            <div>
                                <Descriptions column={1} bordered size="small">
                                    <Descriptions.Item label="Тариф">{subscription.tariffName}</Descriptions.Item>
                                    <Descriptions.Item label="ID подписки">#{subscription.id}</Descriptions.Item>
                                    <Descriptions.Item label="Юридическое лицо">
                                        <Select
                                            value={editLegalEntityId}
                                            onChange={setEditLegalEntityId}
                                            style={{width: '100%'}}
                                            placeholder="Выберите юр. лицо"
                                            showSearch
                                            optionFilterProp="children"
                                        >
                                            {legalEntities.map((le) => (
                                                <Select.Option key={le.id} value={le.id}>{le.name}</Select.Option>
                                            ))}
                                        </Select>
                                    </Descriptions.Item>
                                    <Descriptions.Item label="Тип">
                                        <Select value={editType} onChange={setEditType} style={{width: 200}}>
                                            {SUBSCRIPTION_TYPES.map(t => (
                                                <Select.Option key={t.value} value={t.value}>{t.label}</Select.Option>
                                            ))}
                                        </Select>
                                    </Descriptions.Item>
                                    <Descriptions.Item label="Дата окончания">
                                        <DatePicker
                                            value={editDateEnd}
                                            onChange={setEditDateEnd}
                                            format="DD.MM.YYYY"
                                            style={{width: 200}}
                                            allowClear
                                            placeholder="Бессрочно"
                                        />
                                    </Descriptions.Item>
                                    <Descriptions.Item label="Специальная цена">
                                        <InputNumber
                                            value={editCustomPrice}
                                            onChange={(v) => setEditCustomPrice(v)}
                                            style={{width: 200}}
                                            min={0}
                                            placeholder="Оставить цену тарифа"
                                        />
                                    </Descriptions.Item>
                                </Descriptions>
                                <Row type="flex" gutter={8} justify="center" style={{marginTop: 16}}>
                                    <Col><Button type="primary" loading={saving} onClick={handleSave}>Сохранить</Button></Col>
                                    <Col><Button onClick={cancelEditing}>Отмена</Button></Col>
                                </Row>
                            </div>
                        ) : (
                            <div>
                                <Descriptions column={1} bordered size="small">
                                    <Descriptions.Item label="Юридическое лицо">
                                        {subscription.legalEntityName ? subscription.legalEntityName : ('#' + subscription.legalEntityId)}
                                    </Descriptions.Item>
                                    <Descriptions.Item label="ID подписки">#{subscription.id}</Descriptions.Item>
                                    <Descriptions.Item label="Тариф">{subscription.tariffName}</Descriptions.Item>
                                    <Descriptions.Item label="Описание">{subscription.tariffDescription}</Descriptions.Item>
                                    <Descriptions.Item label="Тип">
                                        <Tag>{subscription.typeName}</Tag>
                                    </Descriptions.Item>
                                    <Descriptions.Item label="Цена">
                                        {subscription.price.toLocaleString('ru-RU')} ₽
                                        {subscription.customPrice !== null && subscription.customPrice !== undefined && (
                                            <span style={{color: '#999', marginLeft: 8}}>
                                                (кастом: {subscription.customPrice.toLocaleString('ru-RU')} ₽)
                                            </span>
                                        )}
                                    </Descriptions.Item>
                                    <Descriptions.Item label="Дата начала">{subscription.dateStart}</Descriptions.Item>
                                    <Descriptions.Item label="Дата окончания">
                                        {subscription.dateEnd || 'Бессрочно'}
                                    </Descriptions.Item>
                                    <Descriptions.Item label="Статус">
                                        {subscription.isActive
                                            ? <Tag color="green">Активна</Tag>
                                            : (subscription.isWorking ? <Tag color="orange">Необходимо продление</Tag> : <Tag color="red">Неактивна</Tag>)}
                                    </Descriptions.Item>
                                </Descriptions>
                                <Row type="flex" justify="center" gutter={8} style={{marginTop: 16}}>
                                    <Col>
                                        <Button type="primary" onClick={startEditing}>
                                            Редактировать
                                        </Button>
                                    </Col>
                                    <Col>
                                        <Button
                                            icon={<ClockCircleOutlined/>}
                                            onClick={() => setIsShowSubscriptionHistory(true)}
                                        >
                                            История
                                        </Button>
                                    </Col>
                                </Row>
                            </div>
                        )}
                    </div>
                ) : (
                    !loading && (
                        <div>
                            <div style={{textAlign: 'center', padding: '0 0 16px', fontSize: 16, color: '#666'}}>
                                Нет активной подписки. Заполните данные для оформления:
                            </div>
                            <Form layout="vertical">
                                <Form.Item label="Юридическое лицо" required>
                                    {partnerId && legalEntities.length > 0 ? (
                                        <Select
                                            value={formLegalEntityId}
                                            onChange={setFormLegalEntityId}
                                            placeholder="Выберите юр. лицо"
                                            showSearch
                                            optionFilterProp="children"
                                        >
                                            {legalEntities.map((le) => (
                                                <Select.Option key={le.id} value={le.id}>{le.name}</Select.Option>
                                            ))}
                                        </Select>
                                    ) : (
                                        <Select value={legalEntityId} disabled>
                                            <Select.Option value={legalEntityId}>
                                                {legalEntityName ? legalEntityName : ('#' + legalEntityId)}
                                            </Select.Option>
                                        </Select>
                                    )}
                                </Form.Item>
                                <Form.Item label="Тариф" required>
                                    <Select
                                        value={formTariffId}
                                        onChange={setFormTariffId}
                                        placeholder="Выберите тариф"
                                    >
                                        {tariffs.map((t) => (
                                            <Select.Option key={t.id} value={t.id}>
                                                {t.name} — {t.price.toLocaleString('ru-RU')} ₽
                                            </Select.Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                                <Form.Item label="Тип подписки" required>
                                    <Select value={formType} onChange={setFormType}>
                                        {SUBSCRIPTION_TYPES.map((t) => (
                                            <Select.Option key={t.value} value={t.value}>{t.label}</Select.Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                                <Form.Item label="Специальная цена (₽)">
                                    <InputNumber
                                        value={formCustomPrice}
                                        onChange={(v) => setFormCustomPrice(v)}
                                        style={{width: '100%'}}
                                        min={0}
                                        placeholder="Использовать цену тарифа"
                                    />
                                </Form.Item>
                                <Row gutter={16}>
                                    <Col span={12}>
                                        <Form.Item label="Дата начала">
                                            <DatePicker
                                                value={formDateStart}
                                                onChange={setFormDateStart}
                                                format="DD.MM.YYYY"
                                                style={{width: '100%'}}
                                                allowClear={false}
                                            />
                                        </Form.Item>
                                    </Col>
                                    <Col span={12}>
                                        <Form.Item label="Пробный период до">
                                            <DatePicker
                                                value={formDateEnd}
                                                onChange={setFormDateEnd}
                                                format="DD.MM.YYYY"
                                                style={{width: '100%'}}
                                                allowClear
                                                placeholder="Бессрочно"
                                            />
                                        </Form.Item>
                                    </Col>
                                </Row>
                                <Row type="flex" justify="center" style={{marginTop: 8}}>
                                    <Col>
                                        <Button
                                            type="primary"
                                            loading={subscribing}
                                            onClick={handleSubscribe}
                                        >
                                            Оформить подписку
                                        </Button>
                                    </Col>
                                </Row>
                            </Form>
                        </div>
                    )
                )}

                {/* Текущее состояние депозита */}
                <Card
                    title="Депозит"
                    style={{marginTop: 16}}
                    extra={deposit && (
                        <Button
                            icon={<ClockCircleOutlined/>}
                            onClick={() => setIsShowDepositHistory(true)}
                        >
                            История
                        </Button>
                    )}
                >
                    {deposit ? (
                        <Descriptions column={1} bordered size="small">
                            <Descriptions.Item label="Сумма">
                                {formatAmount(deposit.amount)}
                            </Descriptions.Item>
                            <Descriptions.Item label="Дата создания">
                                {or(deposit.createdAt, '—')}
                            </Descriptions.Item>
                            <Descriptions.Item label="Дата обновления">
                                {or(deposit.updatedAt, '—')}
                            </Descriptions.Item>
                        </Descriptions>
                    ) : (
                        <div>Депозит не найден</div>
                    )}
                </Card>

                <ModalDepositHistory
                    depositId={deposit ? deposit.id : null}
                    isShow={isShowDepositHistory}
                    setIsShow={setIsShowDepositHistory}
                />

                <ModalSubscriptionHistory
                    subscriptionId={subscription ? subscription.id : null}
                    isShow={isShowSubscriptionHistory}
                    setIsShow={setIsShowSubscriptionHistory}
                />

                {/* Активные (неоплаченные) счета */}
                <Card
                    title="Счета"
                    style={{marginTop: 16}}
                    extra={
                        <div>
                            <Button
                                icon={<ClockCircleOutlined/>}
                                size="small"
                                style={{marginRight: 8}}
                                onClick={() => setIsShowInvoicesHistory(true)}
                            >
                                История
                            </Button>
                            {subscription && (
                                <Button
                                    type="primary"
                                    size="small"
                                    loading={creatingInvoice}
                                    onClick={handleCreateInvoice}
                                >
                                    Выставить счёт
                                </Button>
                            )}
                        </div>
                    }
                >
                    <Table
                        rowKey="id"
                        dataSource={invoices}
                        pagination={false}
                        size="small"
                        locale={{emptyText: 'Нет активных счетов'}}
                        columns={[
                            {
                                title: 'Номер',
                                dataIndex: 'number',
                                key: 'number',
                            },
                            {
                                title: 'Сумма',
                                dataIndex: 'amount',
                                key: 'amount',
                                render: (value) => formatAmount(value),
                            },
                            {
                                title: 'Статус',
                                dataIndex: 'status',
                                key: 'status',
                                render: (status) => (
                                    status === 'paid'
                                        ? <Tag color="green">Оплачен</Tag>
                                        : <Tag color="orange">Ожидает оплаты</Tag>
                                ),
                            },
                            {
                                title: 'Дата создания',
                                dataIndex: 'createdAt',
                                key: 'createdAt',
                                render: (value) => or(value, '—'),
                            },
                            {
                                title: '',
                                key: 'actions',
                                render: (_, record) => (
                                    <div>
                                        {record.status === 'pending' && (
                                            <Button
                                                type="link"
                                                size="small"
                                                loading={markingInvoiceId === record.id}
                                                onClick={() => handleMarkPaid(record)}
                                            >
                                                Оплачено
                                            </Button>
                                        )}
                                        {record.status === 'pending' && (
                                            <Button
                                                type="link"
                                                size="small"
                                                loading={cancellingInvoiceId === record.id}
                                                onClick={() => handleCancelInvoice(record)}
                                                style={{color: '#ff4d4f'}}
                                            >
                                                Отменить
                                            </Button>
                                        )}
                                        <Button
                                            type="link"
                                            size="small"
                                            href={record.fileUrl}
                                            target="_blank"
                                            disabled={!record.fileUrl}
                                        >
                                            Скачать
                                        </Button>
                                    </div>
                                ),
                            },
                        ]}
                    />
                </Card>

                <ModalInvoicesHistory
                    legalEntityId={legalEntityId}
                    isShow={isShowInvoicesHistory}
                    setIsShow={setIsShowInvoicesHistory}
                />
            </Spin>
        </Modal>
    );
}